(() => {
  if (customElements.get('lumi-motion')) return;

  const sections = '.lumi-mediatext,.ls-editorial,.lumi-spec,.lumi-enrichment,.lumi-manufacturer,.lumi-faq,.ls-social,.lumi-carousel,.rich-text,.lumi-campaign__copy';
  const media = '.ls-editorial__visual,.lumi-mediatext__media,.lumi-usage__media,.lumi-spec__media--formula';
  const excluded = 'product-info,media-gallery,product-modal,cart-drawer,dialog,[role="dialog"],[data-lumi-motion="off"]';

  class LumiMotion extends HTMLElement {
    connectedCallback() {
      if (this.ready || !window.IntersectionObserver) return;
      this.ready = true;
      this.seen = new WeakSet();
      this.targets = new Set();
      this.images = new Map();
      this.visible = new Set();
      this.animations = new Map();
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.onScroll = () => this.schedule();
      this.onPreference = () => { this.clear(); this.scan(document); };
      this.onLoad = event => this.scan(event.target);
      this.onUnload = event => this.removeWithin(event.target);
      this.onSelect = event => this.finishWithin(event.target);
      this.onFocus = event => this.finishWithin(event.target.closest(sections));
      this.reveals = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const element = entry.target;
          this.reveals.unobserve(element);
          if (this.seen.has(element)) continue;
          this.seen.add(element);
          if (this.preference.matches || element.contains(document.activeElement) || !element.animate) continue;
          const animation = element.animate([
            { opacity: 0, transform: 'translateY(16px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
          this.animations.set(element, animation);
          animation.finished.then(() => this.animations.delete(element), () => this.animations.delete(element));
        }
      }, { threshold: 0, rootMargin: '0px 0px -32px 0px' });
      this.mediaObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        }
        this.schedule();
      });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.preference.addEventListener('change', this.onPreference);
      document.addEventListener('shopify:section:load', this.onLoad);
      document.addEventListener('shopify:section:unload', this.onUnload);
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('focusin', this.onFocus);
      this.scan(document);
    }

    scan(root) {
      if (this.preference.matches) return;
      const within = selector => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
      for (const element of within(sections)) {
        if (this.targets.has(element) || !element.closest('#MainContent') || element.closest(excluded)) continue;
        if (element.parentElement?.closest(sections) || element.matches('.scroll-trigger') || element.querySelector('.scroll-trigger:not(.scroll-trigger--cancel)')) continue;
        const style = getComputedStyle(element);
        if (style.position === 'sticky' || style.transform !== 'none' || style.animationName !== 'none' || element.querySelector('[data-sticky],.sticky-atc')) continue;
        this.targets.add(element);
        const rect = element.getBoundingClientRect();
        if (rect.top < innerHeight || this.seen.has(element)) this.seen.add(element);
        else this.reveals.observe(element);
      }
      for (const frame of within(media)) {
        if (this.images.has(frame) || !frame.closest('#MainContent') || frame.closest(excluded) || frame.querySelector('video,iframe,lumi-campaign-video')) continue;
        const image = frame.querySelector('img');
        if (!image || image.closest('a,button') || frame.querySelectorAll('img').length !== 1) continue;
        const style = getComputedStyle(image);
        if (style.transform !== 'none' || (style.scale !== 'none' && style.scale !== '1') || style.animationName !== 'none') continue;
        this.images.set(frame, { image, scale: image.style.scale });
        frame.classList.add('lumi-motion-clip');
        image.classList.add('lumi-motion-image');
        this.mediaObserver.observe(frame);
      }
    }

    schedule() {
      if (this.frame || this.preference.matches || !this.visible.size) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        const updates = [];
        for (const frame of this.visible) {
          const item = this.images.get(frame);
          if (!item || !frame.isConnected) continue;
          const rect = frame.getBoundingClientRect();
          const progress = Math.max(0, Math.min(1, (rect.top + rect.height) / (innerHeight + rect.height)));
          updates.push([item.image, (1 + progress * 0.035).toFixed(4)]);
        }
        for (const [image, scale] of updates) image.style.scale = scale;
      });
    }

    finishWithin(root) {
      if (!root) return;
      for (const element of this.targets) {
        if (root !== element && !root.contains(element)) continue;
        this.seen.add(element);
        this.reveals.unobserve(element);
        this.animations.get(element)?.finish();
      }
    }

    removeWithin(root) {
      for (const element of this.targets) {
        if (root !== element && !root.contains(element)) continue;
        this.reveals.unobserve(element);
        this.animations.get(element)?.cancel();
        this.animations.delete(element);
        this.targets.delete(element);
      }
      for (const [frame, item] of this.images) {
        if (root !== frame && !root.contains(frame)) continue;
        this.mediaObserver.unobserve(frame);
        this.visible.delete(frame);
        frame.classList.remove('lumi-motion-clip');
        item.image.classList.remove('lumi-motion-image');
        item.image.style.scale = item.scale;
        this.images.delete(frame);
      }
    }

    clear() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.removeWithin(document);
      this.reveals.disconnect();
      this.mediaObserver.disconnect();
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.clear();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.preference.removeEventListener('change', this.onPreference);
      document.removeEventListener('shopify:section:load', this.onLoad);
      document.removeEventListener('shopify:section:unload', this.onUnload);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('focusin', this.onFocus);
      this.ready = false;
    }
  }

  customElements.define('lumi-motion', LumiMotion);
})();
